import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useModalClose from "../../../hooks/useModalClose";
import closeBtn from "../../../assets/icons/close.png";
import "../../../assets/css/NonLgvModal/nonLgvModalStyles.css";
import { setIsNonLgvModalOpen } from "../../../store/plantSlice";
import LocationList from "./LocationList";
import { isFiltersEnabled, matches } from "../../FilterDrawer/utils";
import ExportButton from "../../Reusables/ExportButton/ExportButton";
import { Flex } from "antd";
import {
  blockRackTableDataValidation,
  getTimeBasedOnUserTime
} from "../../../util/helper/helperFunctions";
import moment from "moment";
import NonLGVInventoryTable from "./NonLGVInventoryTable";
import ColumnSearchFields from "./ColumnSearchFields";
import useTableSearch from "../../../hooks/useTableSearch";

export const columns = [
  { label: "Location", key: "location", enableSort: true, enableSearch: true },
  { label: "LPN", key: "lpn", enableSort: true, enableSearch: true },
  { label: "Item", key: "sku", enableSort: true, enableSearch: true },
  { label: "Item Type", key: "itemType", enableSort: true, enableSearch: true },
  { label: "Quantity", key: "quantity", enableSort: true, enableSearch: false },
  { label: "Lot", key: "lot", enableSort: true, enableSearch: true },
  {
    label: "Production Date",
    key: "productionDate",
    enableSort: true,
    enableSearch: true
  },
  {
    label: "Expiration Date",
    key: "expirationDate",
    enableSort: true,
    enableSearch: true
  },
  {
    label: "Position",
    key: "position",
    enableSort: false,
    enableSearch: false
  },
  { label: "Row", key: "row", enableSort: false, enableSearch: false },
  { label: "Level", key: "level", enableSort: false, enableSearch: false }
];

// convert the name from "C.E.001" to "C.E", "TAC201" to "TAC201"
const transformRawData = (data) => {

  const normalizedData = data.map((item) => {
    const obj = {};
    for (const key of Object.keys(item)) {
      const value = item[key];

      if (key === "productionDate" || key === "expirationDate") {
        obj[key] = !!blockRackTableDataValidation(value)
          ? moment(getTimeBasedOnUserTime(value)).format("YYYY-MM-DD HH:mm:ss")
          : "";
      } else {
        obj[key] = blockRackTableDataValidation(value);
      }
    }

    return obj;
  });

  // Grouping the records by location
  const groupedByCategory = Object.groupBy(normalizedData, ({ location }) =>
    location?.split(".").length > 1
      ? location.split(".").slice(0, 2).join(".")
      : location
  );

  return { allCategoryData: normalizedData, groupedByCategory };
};

const MODAL_ID = "lgv-wrapper-id";

const NonLgvModal = () => {
  const dispatch = useDispatch();
  const nonLgvData = useSelector((state) => state.plant.nonLGVInventory.data);
  const filterResults = useSelector((state) => state.inventory.results);
  const {
    location: selectedLocation,
    sku: selectedSKU,
    lpn: selectedLPN,
    qualityStatus: selectedQualityStatus
  } = useSelector((state) => state.inventory.filters);


  //holds category wise grouped data with values transformed as per the table view form.
  const [transformedGroupedData, setTransformedGroupedData] = useState({});
  const [transformedAllCategoryData, setTransFormedAllCategoryData] = useState(
    []
  );

  const [selectedBlockKey, setSelectedBlockKey] = useState(null);
  const [selectedInventoryData, setSelectedInventoryData] = useState(null);
  const [exportData, setExportData] = useState([]);
  const updateNonLgvExportData = useCallback((data) => {
    setExportData(data);
  }, []);

  useEffect(() => {
    document.body.classList.add("prevent-scroll");

    // Check if there are nonLGV items
    const hasNonLGVItem = filterResults.some(
      (item) => item.storageType === "nonLGV"
    );

    if (!hasNonLGVItem) {
      const { groupedByCategory, allCategoryData } =
        transformRawData(nonLgvData);
      setTransformedGroupedData(groupedByCategory);
      setTransFormedAllCategoryData(allCategoryData);
    } else if (isFiltersEnabled()) {
      const filteredItems = nonLgvData.filter((item) =>
        matches(item, {
          selectedLocation,
          selectedLPN,
          selectedQualityStatus,
          selectedSKU
        })
      );
      const { groupedByCategory, allCategoryData } =
        transformRawData(filteredItems);
      setTransformedGroupedData(groupedByCategory);
      setTransFormedAllCategoryData(allCategoryData);
    }
  }, [
    nonLgvData,
    filterResults,
    selectedLocation,
    selectedLPN,
    selectedQualityStatus,
    selectedSKU
  ]);

  useEffect(() => {
    if (Object.keys(transformedGroupedData).length === 0) return;

    const hasDataForSelected =
      selectedBlockKey !== null && transformedGroupedData[selectedBlockKey];

    if (hasDataForSelected) {
      //retaining data selected location when new data flows
      setSelectedInventoryData(transformedGroupedData[selectedBlockKey]);
    } else {
      setSelectedBlockKey(Object.keys(transformedGroupedData)[0]);
      setSelectedInventoryData(
        transformedGroupedData[Object.keys(transformedGroupedData)[0]]
      );
    }
  }, [transformedGroupedData, selectedBlockKey]);

  const closeModalHandler = () => {
    dispatch(setIsNonLgvModalOpen(false));
    document.body.classList.remove("prevent-scroll");
  };

  const lgvListItemHandler = useCallback((location, data) => {
    setSelectedBlockKey(location);
    setSelectedInventoryData(data);
  }, []);

  const dataEntries = useMemo(
    () => Object.entries(transformedGroupedData),
    [transformedGroupedData]
  );

  useModalClose(MODAL_ID, closeModalHandler);


  const {
    updateFilter,
    clearFilters,
    filteredData,
    isTableSearchFiltersEnabled
  } = useTableSearch(transformedAllCategoryData);

  const tableColumns = useMemo(() => {
    if (!isTableSearchFiltersEnabled) {
      return columns.filter((col) => {
        if (col.key === "location" && !isTableSearchFiltersEnabled)
          return false;
        return true;
      });
    }
    return columns;
  }, [isTableSearchFiltersEnabled]);


  return (
    <div id={MODAL_ID} className="popup-wrapper">
      <div className="popup-container">
        <div className="popup-header">
          <div className="popup-title">Non LGV Inventories</div>
          <div className="popup-close">
            <div
              onClick={closeModalHandler}
              className="close-btn"
              id="close-btn"
            >
              <img src={closeBtn} alt="close button" />
            </div>
          </div>
        </div>
        {nonLgvData && nonLgvData.length !== 0 ? (
          <Flex vertical gap="small" style={{ height: "90%" }}>
            <Flex justify="flex-end" gap="small" align="center">
              <ColumnSearchFields
                columns={columns}
                updateFilter={updateFilter}
                clearFilters={clearFilters}
                isTableSearchFiltersEnabled={isTableSearchFiltersEnabled}
              />
              <ExportButton data={exportData} type={selectedBlockKey} />
            </Flex>

            <div className="popup-bottom">
              {
                !isTableSearchFiltersEnabled &&
                <div className="left-selector-section">
                  <LocationList
                    dataEntries={dataEntries}
                    selectedBlockKey={selectedBlockKey}
                    lgvListItemHandler={lgvListItemHandler}
                  />
                </div>
              }
              <div className="vertical-scroller">
                {selectedInventoryData && (
                  <NonLGVInventoryTable
                    data={
                      isTableSearchFiltersEnabled
                        ? filteredData
                        : selectedInventoryData
                    }
                    updateNonLgvExportData={updateNonLgvExportData}
                    dataTestID="non-lgv-block-table"
                    id="block-location-table-block-rack-hof"
                    columns={tableColumns}
                    isTableSearchFiltersEnabled={isTableSearchFiltersEnabled}
                  />
                )}
              </div>
            </div>
          </Flex>
        ) : (
          <div className="popup-bottom flex-center">
            <div className="non-lgv-table-loader">
              <h4>No data available!</h4>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(NonLgvModal);
